import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { CreditCard, Calendar, Clock, AlertTriangle, CheckCircle, XCircle, Crown, RefreshCw, ChevronDown, ChevronUp, Zap, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'text-emerald-700 bg-emerald-50 border-emerald-200', icon: CheckCircle },
  trialing: { label: 'Trial', color: 'text-blue-700 bg-blue-50 border-blue-200', icon: Clock },
  cancelling: { label: 'Cancelling', color: 'text-amber-700 bg-amber-50 border-amber-200', icon: AlertTriangle },
  past_due: { label: 'Past Due', color: 'text-red-700 bg-red-50 border-red-200', icon: XCircle },
  cancelled: { label: 'Cancelled', color: 'text-gray-700 bg-gray-50 border-gray-200', icon: XCircle },
  none: { label: 'Free', color: 'text-gray-500 bg-gray-50 border-gray-200', icon: CreditCard },
};

const PLAN_LABELS = {
  monthly: 'Monthly',
  annual: 'Annual',
  lifetime: 'Lifetime Deal',
};

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return '—'; }
}

export function SubscriptionSection() {
  const { user, fetchUser } = useAuth();
  const [sub, setSub] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [ltdInfo, setLtdInfo] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);

  const handleOpenPortal = async () => {
    setOpeningPortal(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/stripe/customer-portal`, {
        origin_url: window.location.origin,
      }, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open Stripe portal');
      setOpeningPortal(false);
    }
  };

  const fetchSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [subRes, txnRes, plansRes] = await Promise.all([
        axios.get(`${API}/stripe/my-subscription`, { headers }),
        axios.get(`${API}/stripe/billing-history`, { headers }),
        axios.get(`${API}/stripe/plans`),
      ]);
      setSub(subRes.data);
      setTransactions(txnRes.data.transactions || []);
      if (plansRes.data?.plans?.lifetime) {
        setLtdInfo(plansRes.data.plans.lifetime);
      }
    } catch {
      toast.error('Failed to load subscription info');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSubscription(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/stripe/cancel-subscription`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data.message);
      setShowConfirm(false);
      await fetchUser();
      await fetchSubscription();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/stripe/reactivate-subscription`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data.message);
      await fetchUser();
      await fetchSubscription();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reactivate');
    } finally {
      setReactivating(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">Subscription</h3>
        <div className="animate-pulse space-y-2">
          <div className="h-16 bg-gray-100 rounded-xl" />
          <div className="h-10 bg-gray-100 rounded-xl" />
        </div>
      </div>
    );
  }

  const status = sub?.subscription_status || 'none';
  const plan = sub?.subscription_plan;
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.none;
  const StatusIcon = cfg.icon;
  const isLifetime = plan === 'lifetime';
  const canCancel = (status === 'active' || status === 'trialing') && !isLifetime;
  const canReactivate = status === 'cancelling';
  const showUpgradeCard = !isLifetime && ltdInfo && ltdInfo.ltd_remaining > 0 && 
    (status === 'active' || status === 'trialing' || status === 'cancelling');

  // Savings calculation
  const annualCostPerYear = 59.88;
  const monthlyCostPerYear = 83.88;
  const ltdPrice = ltdInfo?.amount || 49.99;
  const savingsVsAnnual = Math.round((annualCostPerYear * 2) - ltdPrice); // 2 years comparison
  const spotsPercent = ltdInfo ? Math.round((1 - ltdInfo.ltd_remaining / ltdInfo.ltd_total) * 100) : 0;

  const handleUpgradeToLifetime = async () => {
    setUpgrading(true);
    try {
      const token = localStorage.getItem('token');
      const origin = window.location.origin;
      const res = await axios.post(`${API}/stripe/create-checkout`, {
        plan_id: 'lifetime',
        origin_url: origin,
      }, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
      setUpgrading(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="subscription-section">
      <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">Subscription</h3>

      {/* Current plan card */}
      <div className="p-4 bg-white rounded-xl border border-gray-200 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isLifetime ? (
              <Crown className="w-5 h-5 text-amber-600" />
            ) : (
              <CreditCard className="w-5 h-5 text-gray-400" />
            )}
            <div>
              <p className="font-bold text-gray-900" data-testid="current-plan-name">
                {PLAN_LABELS[plan] || plan || 'Free Plan'}
              </p>
              {status === 'cancelling' && sub?.subscription_end_date && (
                <p className="text-xs text-amber-600">Access until {formatDate(sub.subscription_end_date)}</p>
              )}
            </div>
          </div>
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.color}`} data-testid="subscription-status-badge">
            <StatusIcon className="w-3 h-3" />
            {cfg.label}
          </span>
        </div>

        {/* Date details */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          {status === 'trialing' && sub?.trial_end_date && (
            <div className="flex items-center gap-1.5 text-blue-600">
              <Clock className="w-3.5 h-3.5" />
              <span>Trial ends {formatDate(sub.trial_end_date)}</span>
            </div>
          )}
          {sub?.subscription_end_date && !isLifetime && (
            <div className="flex items-center gap-1.5 text-gray-500">
              <Calendar className="w-3.5 h-3.5" />
              <span>{status === 'cancelling' ? 'Expires' : 'Renews'} {formatDate(sub.subscription_end_date)}</span>
            </div>
          )}
          {isLifetime && (
            <div className="flex items-center gap-1.5 text-amber-600">
              <Crown className="w-3.5 h-3.5" />
              <span>Lifetime access — never expires</span>
            </div>
          )}
        </div>

        {/* Cancel / Reactivate buttons */}
        {canCancel && !showConfirm && (
          <Button
            variant="ghost"
            onClick={() => setShowConfirm(true)}
            className="w-full h-9 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
            data-testid="cancel-subscription-btn"
          >
            Cancel Subscription
          </Button>
        )}

        {showConfirm && (
          <div className="p-3 bg-red-50 rounded-lg border border-red-200 space-y-2" data-testid="cancel-confirm-dialog">
            <p className="text-xs text-red-800 font-medium">Are you sure? You'll lose access at the end of your billing period.</p>
            <div className="flex gap-2">
              <Button
                onClick={handleCancel}
                disabled={cancelling}
                className="flex-1 h-8 text-xs bg-red-600 hover:bg-red-700 text-white rounded-lg"
                data-testid="confirm-cancel-btn"
              >
                {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
              </Button>
              <Button
                variant="ghost"
                onClick={() => setShowConfirm(false)}
                className="flex-1 h-8 text-xs rounded-lg"
                data-testid="keep-subscription-btn"
              >
                Keep Subscription
              </Button>
            </div>
          </div>
        )}

        {canReactivate && (
          <Button
            onClick={handleReactivate}
            disabled={reactivating}
            className="w-full h-9 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
            data-testid="reactivate-subscription-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${reactivating ? 'animate-spin' : ''}`} />
            {reactivating ? 'Reactivating...' : 'Reactivate Subscription'}
          </Button>
        )}

        {/* Stripe Customer Portal — self-service card update, invoices, cancel */}
        {!isLifetime && status !== 'none' && (
          <Button
            variant="outline"
            onClick={handleOpenPortal}
            disabled={openingPortal}
            className="w-full h-9 text-xs rounded-lg"
            data-testid="manage-subscription-portal-btn"
          >
            <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
            {openingPortal ? 'Opening Stripe…' : 'Manage in Stripe Portal'}
          </Button>
        )}
      </div>

      {/* Billing History */}
      {transactions.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="w-full flex items-center justify-between p-3 text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
            data-testid="billing-history-toggle"
          >
            <span>Billing History ({transactions.length})</span>
            {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {showHistory && (
            <div className="border-t border-gray-100" data-testid="billing-history-list">
              {transactions.map((txn, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2.5 text-xs border-b border-gray-50 last:border-b-0">
                  <div>
                    <p className="text-gray-800 font-medium">{PLAN_LABELS[txn.plan_id] || txn.plan_id || 'Payment'}</p>
                    <p className="text-gray-400">{formatDate(txn.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-medium text-gray-800">
                      {txn.currency === 'gbp' ? '\u00A3' : '$'}{txn.amount?.toFixed(2) || '—'}
                    </p>
                    <p className={`text-[10px] ${txn.payment_status === 'paid' ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {txn.payment_status || 'pending'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Upgrade to Lifetime prompt */}
      {showUpgradeCard && (
        <div className="relative p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200 overflow-hidden" data-testid="upgrade-lifetime-card">
          {/* Spots remaining bar */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-amber-100">
            <div className="h-full bg-amber-500 transition-all" style={{ width: `${spotsPercent}%` }} />
          </div>

          <div className="flex items-start gap-3 mt-1">
            <div className="shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <Crown className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-gray-900 text-sm">Upgrade to Lifetime</p>
              <p className="text-xs text-gray-600 mt-0.5">
                One payment of <strong>&pound;{ltdPrice.toFixed(2)}</strong> — never pay again.
              </p>
              <p className="text-xs text-emerald-700 font-medium mt-1">
                Save &pound;{savingsVsAnnual} vs 2 years of Annual
                {plan === 'monthly' && ` or \u00A3${Math.round(monthlyCostPerYear * 2 - ltdPrice)} vs Monthly`}
              </p>

              <div className="flex items-center gap-2 mt-2.5">
                <Button
                  onClick={handleUpgradeToLifetime}
                  disabled={upgrading}
                  className="h-8 px-4 text-xs font-semibold bg-amber-500 hover:bg-amber-600 text-white rounded-lg shadow-sm"
                  data-testid="upgrade-lifetime-btn"
                >
                  <Zap className="w-3.5 h-3.5 mr-1" />
                  {upgrading ? 'Redirecting...' : 'Upgrade Now'}
                </Button>
                <span className="text-[10px] text-amber-700 font-medium" data-testid="ltd-spots-remaining">
                  {ltdInfo.ltd_remaining} of {ltdInfo.ltd_total} spots left
                </span>
              </div>

              <p className="text-[10px] text-gray-400 mt-2">
                Includes referral program, coaches forum, and priority access to new features.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Change Plan link (fallback for when LTD is sold out) */}
      {!isLifetime && !showUpgradeCard && status !== 'none' && (
        <a
          href="/onboarding?step=plan"
          className="block text-center text-xs text-emerald-600 hover:text-emerald-700 font-medium py-2"
          data-testid="change-plan-link"
        >
          Change Plan
        </a>
      )}
    </div>
  );
}
